import { memo } from "react";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CalendarDays, MapPin } from "lucide-react";
import { Event } from "@/lib/types";
import { useNavigate } from "react-router-dom";
import { formatInTimeZone } from 'date-fns-tz';

interface EventCardProps {
  event: Event;
}

export const EventCard = memo(({ event }: EventCardProps) => {
  const navigate = useNavigate();

  // Format the date in the event's timezone
  const formattedDate = formatInTimeZone(
    new Date(event.date),
    event.timezone || 'UTC',
    'MMMM d, yyyy h:mm aa'
  );

  const handleNavigateToEvent = () => {
    navigate(`/events/${event.seoUrl}`);
  };

  return (
    <Card className="overflow-hidden bg-gray-900 border-gray-800 hover:border-gray-700 transition-colors card-hover">
      <div className="aspect-video sm:aspect-[16/9] relative overflow-hidden">
        <img
          src={event.imageUrl || "/placeholder.svg"}
          alt={event.title}
          className="object-cover w-full h-full transition-transform hover:scale-105 duration-300"
          loading="lazy"
          width={640}
          height={360}
        />
      </div>
      <CardHeader
        className="space-y-2 px-3 sm:px-6 py-3 sm:py-4 cursor-pointer hover:bg-gray-800 transition-colors touch-feedback"
        onClick={handleNavigateToEvent}
      >
        <h3 className="text-base sm:text-lg md:text-2xl font-semibold line-clamp-2 text-white">
          {event.title}
        </h3>
        {event.subtitle && (
          <p className="text-sm sm:text-base md:text-lg text-gray-300">{event.subtitle}</p>
        )}
      </CardHeader>
      <CardContent className="px-3 sm:px-6 py-2 sm:py-4">
        <div className="space-y-2 sm:space-y-3 md:space-y-4">
          <div className="flex items-center text-gray-300">
            <CalendarDays className="h-4 w-4 sm:h-5 sm:w-5 mr-2 sm:mr-3 flex-shrink-0" />
            <span className="text-xs sm:text-sm md:text-base">{formattedDate}</span>
          </div>
          <div className="flex items-center text-gray-300">
            <MapPin className="h-4 w-4 sm:h-5 sm:w-5 mr-2 sm:mr-3 flex-shrink-0" />
            <span className="text-xs sm:text-sm md:text-base">
              {event.locationUrl ? (
                <a
                  href={event.locationUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-[#9b87f5] transition-colors focus-visible-ring"
                >
                  {event.location}
                </a>
              ) : (
                event.location
              )}
            </span>
          </div>
          <p className="text-xs sm:text-sm md:text-base text-gray-300 line-clamp-3">
            {event.description}
          </p>
        </div>
      </CardContent>
      <CardFooter className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4 px-3 sm:px-6 py-3 sm:py-6">
        <div className="space-y-1 w-full sm:w-auto">
          <span className="text-xs sm:text-sm text-gray-400">From</span>
          <p className="text-sm sm:text-base md:text-xl font-bold text-white">
            ${event.priceGeneralAdmission}
          </p>
        </div>
        <Button
          onClick={handleNavigateToEvent}
          className="w-full sm:w-auto bg-gradient-to-r from-[#9b87f5] to-[#D946EF] hover:opacity-90 text-white text-sm sm:text-base py-2 sm:py-3 touch-feedback focus-visible-ring min-h-[44px] sm:min-h-[unset]"
        >
          Get Tickets
        </Button>
      </CardFooter>
    </Card>
  );
});

EventCard.displayName = "EventCard";
